import { authedFetch } from '@/lib/authed-fetch';
import {
  formatEventPrice,
  formatRegistrationHoldCountdown,
  isPaidEvent,
  isValidEventPriceThb,
  isRegistrationActive,
  isRegistrationHoldActive,
  optimisticRegistrationReducer,
  registrationHoldRemainingMs,
  registrationStatusCopy,
  requiresRegistrationHold,
  MAX_EVENT_PRICE_THB,
  PAYMENT_HOLD_MINUTES,
  type EventPayment,
  type EventPaymentMethod,
  type EventRegistration,
  type EventRegistrationStatus,
  type OptimisticRegistrationAction,
  type OptimisticRegistrationState,
  type RegistrationStatusCopy,
} from '@sixam/shared/event-registration-core';

export {
  formatEventPrice,
  formatRegistrationHoldCountdown,
  isPaidEvent,
  isValidEventPriceThb,
  isRegistrationActive,
  isRegistrationHoldActive,
  optimisticRegistrationReducer,
  registrationHoldRemainingMs,
  registrationStatusCopy,
  requiresRegistrationHold,
  MAX_EVENT_PRICE_THB,
  PAYMENT_HOLD_MINUTES,
  type EventPayment,
  type EventPaymentMethod,
  type EventRegistration,
  type EventRegistrationStatus,
  type OptimisticRegistrationAction,
  type OptimisticRegistrationState,
  type RegistrationStatusCopy,
};

export type CreateRegistrationResponse = Readonly<{
  registration: EventRegistration;
}>;

export type RegistrationFailureReason =
  'full' | 'closed' | 'started' | 'paid' | 'unavailable' | 'checked_in';

export class EventRegistrationApiError extends Error {
  reason: RegistrationFailureReason | null;

  constructor(message: string, reason: RegistrationFailureReason | null = null) {
    super(message);
    this.name = 'EventRegistrationApiError';
    this.reason = reason;
  }
}

async function parseApiResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
  const payload = (await response.json().catch(() => null)) as
    ({ error?: string; reason?: RegistrationFailureReason } & T) | null;

  if (!response.ok) {
    throw new EventRegistrationApiError(payload?.error ?? fallbackMessage, payload?.reason ?? null);
  }

  return payload as T;
}

export async function fetchMyRegistration(eventId: string): Promise<EventRegistration | null> {
  const response = await authedFetch(`/api/events/${eventId}/registrations`);
  return parseApiResponse<EventRegistration | null>(response, 'Could not load registration.');
}

export async function createEventRegistration(
  eventId: string
): Promise<CreateRegistrationResponse> {
  const response = await authedFetch(`/api/events/${eventId}/registrations`, {
    method: 'POST',
  });
  return parseApiResponse<CreateRegistrationResponse>(response, 'Could not create registration.');
}

export async function cancelEventRegistration(eventId: string): Promise<EventRegistration | null> {
  const response = await authedFetch(`/api/events/${eventId}/registrations`, {
    method: 'DELETE',
  });
  return parseApiResponse<EventRegistration | null>(response, 'Could not cancel registration.');
}
